// src/handlers/beerHandlers.js
// ============================================================
// BEER & KEG FILLING HANDLERS
// ============================================================
// Handles all beer-related operations:
// - Filling kegs with new beer
// - Editing beer details
// - Adding ratings (quality feedback)
// - Adding feedback (post-pour comments)
// - Beer history tracking
//
// Key concepts:
// - "Filling" = assigning beer to empty keg + creating history
// - "Rating" = quality score + notes after consuming
// - "Feedback" = keg-kicked status + comments
// ============================================================

import * as SupabaseService from '../supabaseService';

export const createBeerHandlers = (state) => {
  // Destructure only what this handler needs (omit unused values to satisfy eslint)
  const {
    kegs, setKegs,
    selectedKegId,
    newBeer, setNewBeer,
    setBeerImageFile,
    beerImagePreview, setBeerImagePreview,
    setStyleSearch,
    setShowStyleDropdown,
    setShowFillModal,
    setIsEditingBeer,
    account,
    feedbackRating, setFeedbackRating,
    feedbackNotes, setFeedbackNotes,
    feedbackName, setFeedbackName,
    setShowFeedbackModal,
    ratingForm, setRatingForm,
    selectedRatingKeg, setSelectedRatingKeg,
    setShowRatingModal,
    pendingTapStatus, setPendingTapStatus,
    selectedBreweryId,
    locations
  } = state;

  const handleFillKeg = async () => {
    const keg = kegs.find(k => k.id === selectedKegId);
    if (!keg) return;

    const updatedBeer = {
      name: newBeer.name,
      style: newBeer.style,
      brewery: newBeer.brewery || (account?.profile?.name || ''),
      abv: parseFloat(newBeer.abv),
      ibu: newBeer.ibu ? parseInt(newBeer.ibu) : null,
      og: newBeer.og ? parseFloat(newBeer.og) : null,
      fg: newBeer.fg ? parseFloat(newBeer.fg) : null,
      description: newBeer.description,
      imageUrl: beerImagePreview
    };

    const updates = {
      isEmpty: false,
      tapStatus: "Conditioning",
      cleaningStatus: "Purged",
      currentBeer: updatedBeer,
      batchCount: keg.batchCount + 1,
      location: newBeer.location || keg.location
    };

    const result = await SupabaseService.updateKeg(selectedKegId, updates);
    
    if (result.success) {
      const session = {
        kickedDate: new Date().toISOString().split('T')[0],
        location: newBeer.location || keg.location,
        tapStatus: "Conditioning",
        volume: newBeer.batchVolume ? parseFloat(newBeer.batchVolume) : null,
        ratings: []
      };
      await SupabaseService.addBeerHistory(selectedKegId, updatedBeer, session);

      // Increment tap line usage for Line Cleaning maintenance (if location/sublocation has taps)
      try {
        const loc = newBeer.location || keg.location;
        const subloc = keg.sublocation || null;
        let locHasTaps = false;

        if (loc && locations && locations.length > 0) {
          // Find matching location and optional sublocation
          const matchingLoc = locations.find(l => l.name === loc);
          if (matchingLoc) {
            if (subloc) {
              const matchingSub = (matchingLoc.sublocations || []).find(s => s.name === subloc);
              locHasTaps = matchingSub ? !!matchingSub.hasTaps : !!matchingLoc.hasTaps;
            } else {
              locHasTaps = !!matchingLoc.hasTaps;
            }
          }
        }

        if (loc && selectedBreweryId && locHasTaps) {
          // Determine tap name if available (tapNumber on keg is 1-based)
          let tapName = null;
          if (keg.tapNumber) {
            const matchingLoc = locations.find(l => l.name === loc);
            if (matchingLoc) {
              let tapNames = [];
              if (subloc) {
                const matchingSub = (matchingLoc.sublocations || []).find(s => s.name === subloc);
                tapNames = matchingSub?.tapNames || [];
              } else {
                tapNames = matchingLoc.tapNames || [];
              }
              if (tapNames && tapNames.length > 0) {
                const idx = parseInt(keg.tapNumber) - 1;
                tapName = tapNames[idx] || null;
              }
            }
          }

          await SupabaseService.incrementTapLineUsage(selectedBreweryId, loc, subloc, tapName);
        }

        // Increment water filtration batch counter when beer is put on tap
        if (selectedBreweryId) {
          await SupabaseService.incrementWaterFiltrationBatch(selectedBreweryId);
        }
      } catch (e) {
        console.warn('Failed to increment tap line usage:', e);
      }

      const kegsResult = await SupabaseService.fetchKegs();
      if (kegsResult.success !== false) {
        setKegs(kegsResult);
      }

      setShowFillModal(false);
      setStyleSearch("");
      setShowStyleDropdown(false);
      setBeerImageFile(null);
      setBeerImagePreview(null);
      setNewBeer({ name: "", style: "", abv: "", ibu: "", og: "", fg: "", description: "", imageUrl: null, brewery: "", location: "" });
    } else {
      alert('Error filling keg: ' + result.error);
    }
  };

  const handleOpenFillModal = () => {
    setIsEditingBeer(false);
    const selectedKeg = kegs.find(k => k.id === selectedKegId);
    setNewBeer({ name: "", style: "", abv: "", ibu: "", og: "", fg: "", description: "", imageUrl: null, brewery: account?.profile?.name || '', batchVolume: selectedKeg?.kegDetails?.volume || '' });
    setBeerImagePreview(null);
    setStyleSearch("");
    setShowStyleDropdown(false);
    setShowFillModal(true);
  };

  const handleOpenEditBeer = () => {
    const keg = kegs.find(k => k.id === selectedKegId);
    if (!keg || !keg.currentBeer) return;
    const cb = keg.currentBeer;
    setNewBeer({
      name: cb.name || "",
      style: cb.style || "",
      abv: cb.abv || "",
      ibu: cb.ibu || "",
      og: cb.og || "",
      fg: cb.fg || "",
      description: cb.description || "",
      imageUrl: cb.imageUrl || null,
      brewery: cb.brewery || (account?.profile?.name || ''),
      location: keg.location || ""
    });
    setBeerImagePreview(cb.imageUrl || null);
    setStyleSearch("");
    setShowStyleDropdown(false);
    setIsEditingBeer(true);
    setShowFillModal(true);
  };

  const handleUpdateCurrentBeer = async () => {
    const keg = kegs.find(k => k.id === selectedKegId);
    if (!keg) return;

    const updatedBeer = {
      name: newBeer.name,
      style: newBeer.style,
      brewery: newBeer.brewery || (account?.profile?.name || ''),
      abv: parseFloat(newBeer.abv),
      ibu: newBeer.ibu ? parseInt(newBeer.ibu) : null,
      og: newBeer.og ? parseFloat(newBeer.og) : null,
      fg: newBeer.fg ? parseFloat(newBeer.fg) : null,
      description: newBeer.description,
      imageUrl: beerImagePreview
    };

    const result = await SupabaseService.updateKeg(selectedKegId, {
      currentBeer: updatedBeer,
      location: newBeer.location || keg.location
    });

    if (result.success) {
      const kegsResult = await SupabaseService.fetchKegs();
      if (kegsResult.success !== false) {
        setKegs(kegsResult);
      }
      setShowFillModal(false);
      setStyleSearch("");
      setShowStyleDropdown(false);
      setBeerImageFile(null);
      setBeerImagePreview(null);
      setNewBeer({ name: "", style: "", abv: "", ibu: "", og: "", fg: "", description: "", imageUrl: null, brewery: "", location: "" });
      setIsEditingBeer(false);
    } else {
      alert('Error updating beer: ' + result.error);
    }
  };

  const handleSubmitFeedback = async () => {
    const keg = kegs.find(k => k.id === selectedKegId);
    if (!keg || !keg.currentBeer) return;

    const session = {
      kickedDate: new Date().toISOString().split('T')[0],
      location: keg.location,
      tapStatus: keg.tapStatus,
      ratings: feedbackRating > 0 ? [{
        rating: feedbackRating,
        notes: feedbackNotes,
        raterName: feedbackName || 'Anonymous'
      }] : []
    };

    const result = await SupabaseService.addBeerHistory(selectedKegId, keg.currentBeer, session);
    
    if (result.success) {
      const updateResult = await SupabaseService.updateKeg(selectedKegId, {
        isEmpty: true,
        tapStatus: "Empty",
        tapNumber: null,
        cleaningStatus: "Dirty",
        currentBeer: null
      });
      
      if (updateResult.success) {
        const kegsResult = await SupabaseService.fetchKegs();
        if (kegsResult.success !== false) {
          setKegs(kegsResult);
        }
        
        setShowFeedbackModal(false);
        setFeedbackRating(0);
        setFeedbackNotes("");
        setFeedbackName("");
        
        if (pendingTapStatus) {
          const newKeg = kegsResult?.find(k => k.id === pendingTapStatus.kegId);
          if (newKeg) {
            const assignResult = await SupabaseService.updateKeg(
              pendingTapStatus.kegId,
              {
                tapStatus: 'On Tap',
                location: pendingTapStatus.location,
                sublocation: pendingTapStatus.sublocation,
                tapNumber: pendingTapStatus.tapName
              }
            );
            
            if (assignResult.success) {
              const finalKegsResult = await SupabaseService.fetchKegs();
              if (finalKegsResult.success !== false) {
                setKegs(finalKegsResult);
              }
            }
            
            setPendingTapStatus(null);
          }
        }
      }
    } else {
      alert('Error submitting feedback: ' + result.error);
    }
  };

  const handleSubmitRating = async () => {
    if (ratingForm.rating === 0) return;

    const result = await SupabaseService.addRating(selectedRatingKeg.id, {
      rating: ratingForm.rating,
      notes: ratingForm.notes,
      raterName: ratingForm.raterName || 'Anonymous'
    });

    if (result.success) {
      const kegsResult = await SupabaseService.fetchKegs();
      if (kegsResult.success !== false) {
        setKegs(kegsResult);
      }
      
      setShowRatingModal(false);
      setSelectedRatingKeg(null);
      setRatingForm({ rating: 0, notes: '', raterName: '' });
    } else {
      alert('Error submitting rating: ' + result.error);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBeerImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setBeerImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  return {
    handleFillKeg,
    handleOpenFillModal,
    handleOpenEditBeer,
    handleUpdateCurrentBeer,
    handleSubmitFeedback,
    handleSubmitRating,
    handleImageUpload
  };
};
